// 10 SEO articles targeting Dominican Republic searches (Santo Domingo,
// Santiago, Punta Cana). Each has a headings outline (H2s) and an FAQ
// block — both rendered on /learn and emitted as Article + FAQPage
// JSON-LD to win local snippets in Google RD.

export type LearnHeading = { en: string; es: string };
export type LearnFAQ = { q: { en: string; es: string }; a: { en: string; es: string } };

export type LearnArticle = {
  id: string;
  type: "article" | "guide" | "video";
  title: { en: string; es: string };
  desc: { en: string; es: string };
  img: string;
  tag: { en: string; es: string };
  mins: number;
  pids: number[];
  date: string;
  headings?: LearnHeading[];
  faqs?: LearnFAQ[];
  city?: "Santo Domingo" | "Santiago" | "Punta Cana";
};

export const DR_SEO_ARTICLES: LearnArticle[] = [
  {
    id: "dr1", type: "article", img: "🏋️", mins: 7, pids: [1, 14], date: "2026-05-10",
    city: "Santo Domingo",
    tag: { en: "Santo Domingo", es: "Santo Domingo" },
    title: {
      es: "Dónde Comprar Proteína Whey en Santo Domingo (Guía 2026)",
      en: "Where to Buy Whey Protein in Santo Domingo (2026 Guide)",
    },
    desc: {
      es: "Comparamos precios, sellos de autenticidad y entrega de proteína whey en Santo Domingo. Marcas originales, envío en 24h y pago en pesos.",
      en: "Compare prices, authenticity seals and delivery for whey protein in Santo Domingo. Original brands, 24h delivery and DOP payment.",
    },
    headings: [
      { es: "Cómo elegir proteína whey original en RD", en: "How to choose original whey in DR" },
      { es: "Mejores zonas de Santo Domingo con entrega rápida", en: "Best Santo Domingo zones for fast delivery" },
      { es: "Precios promedio en pesos dominicanos (DOP)", en: "Average prices in Dominican pesos (DOP)" },
      { es: "Whey aislada vs concentrada: cuál te conviene", en: "Whey isolate vs concentrate: which fits you" },
    ],
    faqs: [
      { q: { es: "¿Cuánto cuesta una proteína whey en Santo Domingo?", en: "How much does whey protein cost in Santo Domingo?" },
        a: { es: "Entre RD$2,800 y RD$5,500 según marca y tamaño. NutriMarket muestra precios en DOP con envío incluido a Distrito Nacional y Santo Domingo Este.", en: "Between DOP 2,800 and 5,500 depending on brand and size. NutriMarket shows DOP prices including shipping to Distrito Nacional and Santo Domingo Este." } },
      { q: { es: "¿Hacen entrega el mismo día en Santo Domingo?", en: "Do you deliver same-day in Santo Domingo?" },
        a: { es: "Sí, en pedidos confirmados antes de la 1:00 p.m. entregamos el mismo día en Naco, Piantini, Gazcue y Bella Vista.", en: "Yes — orders confirmed before 1:00 p.m. ship same day to Naco, Piantini, Gazcue and Bella Vista." } },
      { q: { es: "¿Cómo sé que la proteína es original?", en: "How do I know the whey is original?" },
        a: { es: "Cada bote trae sello holográfico del distribuidor autorizado y código QR verificable.", en: "Every tub carries the authorized-distributor hologram and a verifiable QR code." } },
    ],
  },
  {
    id: "dr2", type: "guide", img: "💪", mins: 9, pids: [14], date: "2026-05-08",
    city: "Santiago",
    tag: { en: "Santiago", es: "Santiago" },
    title: {
      es: "Mejor Creatina Monohidratada en Santiago de los Caballeros",
      en: "Best Creatine Monohydrate in Santiago de los Caballeros",
    },
    desc: {
      es: "Guía local 2026: precios, dosis y tiendas confiables para comprar creatina monohidratada en Santiago. Envío al Cibao y pago contra entrega.",
      en: "Local 2026 guide: prices, dosing and trusted stores to buy creatine monohydrate in Santiago. Cibao shipping and COD available.",
    },
    headings: [
      { es: "Beneficios reales de la creatina para el clima del Cibao", en: "Real creatine benefits for the Cibao climate" },
      { es: "Cómo tomarla: dosis y fase de carga", en: "How to take it: dose and loading phase" },
      { es: "Tiendas y entrega en Santiago, La Vega y Moca", en: "Stores and delivery in Santiago, La Vega and Moca" },
      { es: "Mitos sobre la creatina y la retención de líquido", en: "Myths about creatine and water retention" },
    ],
    faqs: [
      { q: { es: "¿Llega creatina a Santiago en cuánto tiempo?", en: "How fast does creatine arrive in Santiago?" },
        a: { es: "24-48 horas desde Santo Domingo, con opción de pago contra entrega en toda la provincia Santiago.", en: "24-48 hours from Santo Domingo, with cash-on-delivery across Santiago province." } },
      { q: { es: "¿Es segura la creatina con el calor dominicano?", en: "Is creatine safe in Dominican heat?" },
        a: { es: "Sí. Solo aumenta tu consumo de agua a 3L/día y guárdala en lugar fresco bajo 25°C.", en: "Yes. Just raise water intake to 3L/day and store it below 25°C." } },
      { q: { es: "¿Necesito fase de carga?", en: "Do I need a loading phase?" },
        a: { es: "No es obligatoria. 5g diarios saturan tus músculos en 3-4 semanas sin efectos secundarios.", en: "Not required. 5g daily saturates your muscles in 3-4 weeks with no side effects." } },
    ],
  },
  {
    id: "dr3", type: "article", img: "🌴", mins: 6, pids: [9, 6], date: "2026-05-05",
    city: "Punta Cana",
    tag: { en: "Punta Cana", es: "Punta Cana" },
    title: {
      es: "Suplementos para Vivir en Punta Cana: Calor, Sol y Recuperación",
      en: "Supplements for Living in Punta Cana: Heat, Sun and Recovery",
    },
    desc: {
      es: "Magnesio, electrolitos y ashwagandha: la combinación ideal para quienes entrenan o trabajan en el clima tropical de Punta Cana y Bávaro.",
      en: "Magnesium, electrolytes and ashwagandha: the ideal combo if you train or work in Punta Cana and Bávaro's tropical climate.",
    },
    headings: [
      { es: "Por qué el clima de Punta Cana exige más electrolitos", en: "Why Punta Cana's climate demands more electrolytes" },
      { es: "Magnesio para dormir mejor con la humedad", en: "Magnesium for better sleep in humidity" },
      { es: "Vitamina D: ¿necesaria si vives en el Caribe?", en: "Vitamin D: needed if you live in the Caribbean?" },
      { es: "Envío a Bávaro, Verón y Cap Cana", en: "Delivery to Bávaro, Verón and Cap Cana" },
    ],
    faqs: [
      { q: { es: "¿Llegan a Punta Cana los suplementos de NutriMarket?", en: "Does NutriMarket ship to Punta Cana?" },
        a: { es: "Sí, 48-72h a Bávaro, Verón, Cap Cana y Friusa, con pago en DOP o contra entrega.", en: "Yes, 48-72h to Bávaro, Verón, Cap Cana and Friusa, with DOP or COD payment." } },
      { q: { es: "¿Necesito vitamina D viviendo en Punta Cana?", en: "Do I need vitamin D living in Punta Cana?" },
        a: { es: "Muchos residentes trabajan bajo techo; un análisis de sangre puede mostrar niveles bajos pese al sol.", en: "Many residents work indoors; bloodwork can still show low levels despite the sun." } },
      { q: { es: "¿Cuál electrolito es mejor para el calor tropical?", en: "Which electrolyte is best for tropical heat?" },
        a: { es: "Mezclas con sodio (1000mg+), potasio y magnesio sin azúcar añadida.", en: "Mixes with sodium (1000mg+), potassium and magnesium, no added sugar." } },
    ],
  },
  {
    id: "dr4", type: "guide", img: "🥗", mins: 10, pids: [1, 9], date: "2026-05-02",
    city: "Santo Domingo",
    tag: { en: "Bienestar RD", es: "Bienestar RD" },
    title: {
      es: "Vitaminas para Mujeres en República Dominicana: Guía Completa",
      en: "Vitamins for Women in the Dominican Republic: Complete Guide",
    },
    desc: {
      es: "Hierro, ácido fólico, colágeno y biotina: qué buscar, dosis recomendadas y dónde comprar vitaminas para mujeres en RD con entrega nacional.",
      en: "Iron, folic acid, collagen and biotin: what to look for, recommended doses, and where to buy women's vitamins in DR with nationwide delivery.",
    },
    headings: [
      { es: "Deficiencias más comunes en mujeres dominicanas", en: "Most common deficiencies in Dominican women" },
      { es: "Colágeno marino: piel y cabello en clima tropical", en: "Marine collagen: skin and hair in tropical climate" },
      { es: "Ácido fólico antes y durante el embarazo", en: "Folic acid before and during pregnancy" },
      { es: "Dónde comprar en Santo Domingo y enviar al interior", en: "Where to buy in Santo Domingo and ship inland" },
    ],
    faqs: [
      { q: { es: "¿Cuál es el mejor multivitamínico para mujeres en RD?", en: "What's the best multivitamin for women in DR?" },
        a: { es: "Uno con hierro (18mg), ácido fólico (400mcg), biotina y vitamina D3 — comunes en clima caribeño.", en: "One with iron (18mg), folate (400mcg), biotin and vitamin D3 — common gaps in Caribbean climates." } },
      { q: { es: "¿Puedo combinar colágeno y proteína?", en: "Can I combine collagen and protein?" },
        a: { es: "Sí, son complementarios. Toma colágeno por la mañana y proteína post-entreno.", en: "Yes, they complement. Take collagen in the morning and protein post-workout." } },
      { q: { es: "¿Envían vitaminas a provincias del interior?", en: "Do you ship vitamins to interior provinces?" },
        a: { es: "Sí, a las 31 provincias en 2-4 días con Caribe Pack y pago contra entrega.", en: "Yes, to all 31 provinces in 2-4 days via Caribe Pack with COD." } },
    ],
  },
  {
    id: "dr5", type: "article", img: "☕", mins: 5, pids: [10], date: "2026-04-28",
    city: "Santiago",
    tag: { en: "Energía", es: "Energía" },
    title: {
      es: "Pre-Entreno sin Crash: Opciones en Santiago RD",
      en: "Pre-Workout Without Crash: Options in Santiago DR",
    },
    desc: {
      es: "Cafeína moderada, beta-alanina y citrulina: pre-entrenos limpios disponibles en Santiago, Moca y La Vega. Sin azúcar, sin bajón.",
      en: "Moderate caffeine, beta-alanine and citrulline: clean pre-workouts available in Santiago, Moca and La Vega. No sugar, no crash.",
    },
    headings: [
      { es: "Por qué evitar pre-entrenos con 400mg+ de cafeína", en: "Why avoid pre-workouts with 400mg+ caffeine" },
      { es: "Ingredientes que sí funcionan respaldados por ciencia", en: "Science-backed ingredients that actually work" },
      { es: "Marcas autorizadas en el Cibao", en: "Authorized brands in the Cibao" },
    ],
    faqs: [
      { q: { es: "¿Es seguro un pre-entreno antes de correr en Santiago?", en: "Is pre-workout safe before running in Santiago?" },
        a: { es: "Sí en dosis moderadas (150-200mg cafeína), hidratándote bien por el calor.", en: "Yes at moderate doses (150-200mg caffeine), staying well hydrated due to the heat." } },
      { q: { es: "¿Cuál pre-entreno no genera crash?", en: "Which pre-workout has no crash?" },
        a: { es: "Los que combinan cafeína con L-teanina en proporción 1:2 evitan el bajón energético.", en: "Those combining caffeine with L-theanine at 1:2 ratio avoid the energy crash." } },
    ],
  },
  {
    id: "dr6", type: "guide", img: "🏖️", mins: 8, pids: [1, 14, 6], date: "2026-04-25",
    city: "Punta Cana",
    tag: { en: "Fitness", es: "Fitness" },
    title: {
      es: "Rutina y Suplementos para Ganar Músculo en Punta Cana",
      en: "Routine and Supplements to Build Muscle in Punta Cana",
    },
    desc: {
      es: "Cómo planificar entrenamiento, dieta y stack de suplementos viviendo en Punta Cana o Bávaro. Adaptado al clima, agenda turística y disponibilidad local.",
      en: "How to plan training, diet and supplement stack while living in Punta Cana or Bávaro. Adapted to climate, tourist schedule and local availability.",
    },
    headings: [
      { es: "Stack básico: proteína + creatina + multivitamínico", en: "Basic stack: protein + creatine + multivitamin" },
      { es: "Cómo entrenar en gimnasios de Bávaro y Verón", en: "How to train in Bávaro and Verón gyms" },
      { es: "Comida limpia en zonas hoteleras", en: "Clean food in hotel zones" },
      { es: "Tiempos de entrega a Punta Cana", en: "Delivery times to Punta Cana" },
    ],
    faqs: [
      { q: { es: "¿Puedo entrenar al aire libre en Punta Cana?", en: "Can I train outdoors in Punta Cana?" },
        a: { es: "Sí, antes de las 8 a.m. o después de las 5 p.m. para evitar el pico de UV.", en: "Yes, before 8 a.m. or after 5 p.m. to avoid the UV peak." } },
      { q: { es: "¿Cuánta proteína al día necesito?", en: "How much daily protein do I need?" },
        a: { es: "1.6-2.2 g/kg de peso corporal para ganar músculo, repartida en 3-5 comidas.", en: "1.6-2.2 g/kg bodyweight to build muscle, split across 3-5 meals." } },
    ],
  },
  {
    id: "dr7", type: "article", img: "🌙", mins: 6, pids: [9, 6], date: "2026-04-22",
    city: "Santo Domingo",
    tag: { en: "Sueño", es: "Sueño" },
    title: {
      es: "Suplementos para Dormir Mejor en Santo Domingo",
      en: "Supplements for Better Sleep in Santo Domingo",
    },
    desc: {
      es: "Melatonina, magnesio glicinato y ashwagandha: qué funciona realmente para el ruido y estrés de la capital dominicana.",
      en: "Melatonin, magnesium glycinate and ashwagandha: what actually works for the capital's noise and stress.",
    },
    headings: [
      { es: "Por qué duermes mal en Santo Domingo", en: "Why you sleep badly in Santo Domingo" },
      { es: "Magnesio glicinato vs citrato", en: "Magnesium glycinate vs citrate" },
      { es: "Dosis seguras de melatonina", en: "Safe melatonin doses" },
    ],
    faqs: [
      { q: { es: "¿Es seguro tomar melatonina cada noche?", en: "Is melatonin safe every night?" },
        a: { es: "Dosis de 0.5-3mg son seguras a largo plazo en adultos sanos.", en: "0.5-3mg doses are safe long-term in healthy adults." } },
      { q: { es: "¿Ashwagandha funciona para el estrés?", en: "Does ashwagandha work for stress?" },
        a: { es: "Sí, estudios clínicos muestran reducción de cortisol con KSM-66 a 600mg/día.", en: "Yes, clinical studies show cortisol reduction with KSM-66 at 600mg/day." } },
    ],
  },
  {
    id: "dr8", type: "guide", img: "🥥", mins: 7, pids: [1, 14], date: "2026-04-18",
    city: "Santiago",
    tag: { en: "Pérdida de peso", es: "Pérdida de peso" },
    title: {
      es: "Cómo Bajar de Peso de Forma Saludable en Santiago",
      en: "How to Lose Weight Healthily in Santiago",
    },
    desc: {
      es: "Déficit calórico, proteína alta y suplementos termogénicos seguros para el clima del Cibao. Plan realista con comidas dominicanas.",
      en: "Calorie deficit, high protein and safe thermogenic supplements for Cibao climate. Realistic plan with Dominican meals.",
    },
    headings: [
      { es: "Calcular tu déficit calórico real", en: "Calculate your real calorie deficit" },
      { es: "Sustituir frituras por opciones dominicanas saludables", en: "Swap fried foods for healthy Dominican options" },
      { es: "Termogénicos: cuáles son seguros en RD", en: "Thermogenics: which are safe in DR" },
    ],
    faqs: [
      { q: { es: "¿Funcionan los quemadores de grasa?", en: "Do fat burners work?" },
        a: { es: "Solo como apoyo. La pérdida real viene del déficit calórico y proteína alta.", en: "Only as support. Real loss comes from calorie deficit and high protein." } },
      { q: { es: "¿Puedo comer mangú haciendo dieta?", en: "Can I eat mangú while dieting?" },
        a: { es: "Sí, controlando porciones y acompañándolo con proteína magra como huevos o pollo.", en: "Yes, controlling portions and pairing with lean protein like eggs or chicken." } },
    ],
  },
  {
    id: "dr9", type: "article", img: "🧠", mins: 6, pids: [6], date: "2026-04-15",
    city: "Punta Cana",
    tag: { en: "Focus", es: "Concentración" },
    title: {
      es: "Nootrópicos en Punta Cana: Enfoque para Trabajar Remoto",
      en: "Nootropics in Punta Cana: Focus for Remote Work",
    },
    desc: {
      es: "L-teanina, omega-3 y bacopa para nómadas digitales en Punta Cana y Cap Cana. Productividad sin ansiedad ni crash.",
      en: "L-theanine, omega-3 and bacopa for digital nomads in Punta Cana and Cap Cana. Productivity without anxiety or crash.",
    },
    headings: [
      { es: "Nootrópicos respaldados por evidencia", en: "Evidence-backed nootropics" },
      { es: "Stack diario para 8 horas de trabajo remoto", en: "Daily stack for 8 hours of remote work" },
    ],
    faqs: [
      { q: { es: "¿Son adictivos los nootrópicos?", en: "Are nootropics addictive?" },
        a: { es: "Los naturales como L-teanina y bacopa no crean dependencia.", en: "Natural ones like L-theanine and bacopa are non-addictive." } },
      { q: { es: "¿Mejoran el rendimiento sin café?", en: "Do they boost performance without coffee?" },
        a: { es: "Sí, especialmente bacopa y omega-3 mejoran memoria a largo plazo.", en: "Yes, especially bacopa and omega-3 improve long-term memory." } },
    ],
  },
  {
    id: "dr10", type: "guide", img: "📦", mins: 5, pids: [], date: "2026-04-12",
    city: "Santo Domingo",
    tag: { en: "Envíos", es: "Envíos" },
    title: {
      es: "Cómo Pedir Suplementos Online en República Dominicana",
      en: "How to Order Supplements Online in the Dominican Republic",
    },
    desc: {
      es: "Guía completa: pago en DOP, contra entrega, envío a las 31 provincias, sellos de autenticidad y políticas de devolución en NutriMarket RD.",
      en: "Complete guide: DOP payment, COD, delivery to all 31 provinces, authenticity seals and return policies at NutriMarket DR.",
    },
    headings: [
      { es: "Métodos de pago aceptados en RD", en: "Payment methods accepted in DR" },
      { es: "Tiempos de entrega por provincia", en: "Delivery times by province" },
      { es: "Cómo verificar productos originales", en: "How to verify original products" },
      { es: "Política de devoluciones y reembolsos", en: "Returns and refunds policy" },
    ],
    faqs: [
      { q: { es: "¿Aceptan pago contra entrega?", en: "Do you accept cash on delivery?" },
        a: { es: "Sí, en Distrito Nacional, Santo Domingo, Santiago y Punta Cana sin recargo adicional.", en: "Yes, in Distrito Nacional, Santo Domingo, Santiago and Punta Cana with no extra fee." } },
      { q: { es: "¿Cuánto tarda en llegar a mi provincia?", en: "How long does it take to my province?" },
        a: { es: "1 día en Distrito Nacional, 2 días en Cibao y zona Este, 3-4 días al Sur profundo.", en: "1 day in Distrito Nacional, 2 days in Cibao and East, 3-4 days in deep South." } },
      { q: { es: "¿Puedo devolver un suplemento?", en: "Can I return a supplement?" },
        a: { es: "Sí, sin abrir y dentro de 7 días según nuestra política de devoluciones.", en: "Yes, unopened within 7 days per our returns policy." } },
    ],
  },
  {
    id: "dr11", type: "article", img: "🧴", mins: 7, pids: [], date: "2026-06-01",
    city: "Santo Domingo",
    tag: { en: "Vitaminas", es: "Vitaminas" },
    title: {
      es: "Cómo Elegir un Multivitamínico en RD (Mujer, Hombre, +40)",
      en: "How to Choose a Multivitamin in DR (Women, Men, 40+)",
    },
    desc: {
      es: "Guía 2026 para elegir el multivitamínico correcto en República Dominicana según edad, sexo y nivel de actividad. Comparación de marcas y precios en DOP.",
      en: "2026 guide to choose the right multivitamin in DR by age, sex and activity. Brand and DOP price comparison.",
    },
    headings: [
      { es: "Qué buscar en la etiqueta de un multivitamínico", en: "What to look for on the label" },
      { es: "Multi para mujer: hierro, ácido fólico, B12", en: "Women's multi: iron, folate, B12" },
      { es: "Multi para hombre: zinc, magnesio, vitamina D", en: "Men's multi: zinc, magnesium, vitamin D" },
      { es: "Después de los 40: B12, calcio, omega 3", en: "After 40: B12, calcium, omega 3" },
    ],
    faqs: [
      { q: { es: "¿Realmente necesito un multivitamínico?", en: "Do I really need a multivitamin?" },
        a: { es: "Si tu dieta es variada, no siempre. Pero si entrenas, viajas, comes fuera o tienes deficiencias detectadas en sangre, sí ayuda.", en: "If your diet is varied, not always. But if you train, travel, eat out or have blood-detected deficiencies, yes." } },
      { q: { es: "¿Mañana o noche?", en: "Morning or night?" },
        a: { es: "Mañana con desayuno. Las vitaminas del complejo B pueden activarte demasiado de noche.", en: "Morning with breakfast. B-complex vitamins can be too stimulating at night." } },
    ],
  },
  {
    id: "dr12", type: "guide", img: "☀️", mins: 6, pids: [], date: "2026-05-28",
    city: "Santo Domingo",
    tag: { en: "Vitamina D", es: "Vitamina D" },
    title: {
      es: "Vitamina D en el Caribe: ¿Realmente la Necesitas?",
      en: "Vitamin D in the Caribbean: Do You Really Need It?",
    },
    desc: {
      es: "Aunque vivimos en pleno Caribe, el 40% de los dominicanos tiene deficiencia de vitamina D. Aprende por qué y cuánto suplementar.",
      en: "Even living in the Caribbean, 40% of Dominicans are vitamin D deficient. Learn why and how much to supplement.",
    },
    headings: [
      { es: "Por qué el sol no es suficiente en RD", en: "Why sun isn't enough in DR" },
      { es: "Síntomas de deficiencia comunes", en: "Common deficiency symptoms" },
      { es: "Dosis correcta: 1000-4000 UI según peso", en: "Correct dose: 1000-4000 IU by weight" },
      { es: "Vitamina D3 vs D2: cuál comprar", en: "Vitamin D3 vs D2: which to buy" },
    ],
    faqs: [
      { q: { es: "¿Cuánta vitamina D debo tomar al día?", en: "How much vitamin D per day?" },
        a: { es: "Adultos sanos: 1000-2000 UI. Con deficiencia confirmada: 4000 UI bajo supervisión médica.", en: "Healthy adults: 1000-2000 IU. With confirmed deficiency: 4000 IU under medical supervision." } },
      { q: { es: "¿Puedo combinarla con calcio?", en: "Can I combine with calcium?" },
        a: { es: "Sí, de hecho la vitamina D mejora la absorción del calcio. Excelente combo para huesos.", en: "Yes — vitamin D actually improves calcium absorption. Great bone combo." } },
    ],
  },
  {
    id: "dr13", type: "article", img: "🌙", mins: 8, pids: [], date: "2026-05-22",
    city: "Santo Domingo",
    tag: { en: "Sueño", es: "Sueño" },
    title: {
      es: "Magnesio para Dormir Mejor: Tipos, Dosis y Marcas en RD",
      en: "Magnesium for Better Sleep: Types, Doses and Brands in DR",
    },
    desc: {
      es: "Glicinato, citrato, malato, treonato: comparamos cada tipo de magnesio para dormir, relajar músculos y reducir ansiedad. Precios DOP.",
      en: "Glycinate, citrate, malate, threonate: we compare each magnesium type for sleep, muscle relaxation and anxiety. DOP prices.",
    },
    headings: [
      { es: "Magnesio glicinato: el rey del sueño", en: "Magnesium glycinate: sleep king" },
      { es: "Citrato vs malato vs treonato", en: "Citrate vs malate vs threonate" },
      { es: "Cuándo tomarlo y cuánto", en: "When to take and how much" },
      { es: "Señales de que te falta magnesio", en: "Signs you lack magnesium" },
    ],
    faqs: [
      { q: { es: "¿Cuál es el mejor magnesio para dormir?", en: "Best magnesium for sleep?" },
        a: { es: "Glicinato. Es el más biodisponible para el sistema nervioso y no causa malestar estomacal.", en: "Glycinate. Most bioavailable for the nervous system and no stomach upset." } },
      { q: { es: "¿Cuánto antes de dormir lo tomo?", en: "How long before bed?" },
        a: { es: "30-60 minutos antes de acostarte, con o sin comida.", en: "30-60 minutes before bed, with or without food." } },
    ],
  },
  {
    id: "dr14", type: "article", img: "🐟", mins: 7, pids: [], date: "2026-05-18",
    city: "Santo Domingo",
    tag: { en: "Omega 3", es: "Omega 3" },
    title: {
      es: "Omega 3 en República Dominicana: Dosis Correcta y Mejores Marcas",
      en: "Omega 3 in DR: Correct Dose and Best Brands",
    },
    desc: {
      es: "Comparamos EPA, DHA y omega 3 vegano. Cuánto tomar para corazón, cerebro y articulaciones. Marcas certificadas IFOS disponibles en RD.",
      en: "Compare EPA, DHA and vegan omega 3. How much for heart, brain and joints. IFOS-certified brands available in DR.",
    },
    headings: [
      { es: "EPA vs DHA: qué hace cada uno", en: "EPA vs DHA: what each does" },
      { es: "Dosis para corazón vs cerebro", en: "Heart dose vs brain dose" },
      { es: "Cómo evitar el reflujo a pescado", en: "How to avoid fish burps" },
      { es: "Omega 3 vegano de algas — ¿vale la pena?", en: "Algae-based vegan omega 3 — worth it?" },
    ],
    faqs: [
      { q: { es: "¿Cuánto omega 3 al día?", en: "How much omega 3 per day?" },
        a: { es: "Adultos sanos: 1000-2000mg combinados EPA+DHA. Mayor dosis solo bajo indicación médica.", en: "Healthy adults: 1000-2000mg combined EPA+DHA. Higher only with medical guidance." } },
      { q: { es: "¿Se puede tomar con anticoagulantes?", en: "Can I combine with anticoagulants?" },
        a: { es: "Consulta a tu médico primero — el omega 3 también afecta la coagulación.", en: "Consult your doctor first — omega 3 also affects clotting." } },
    ],
  },
  {
    id: "dr15", type: "guide", img: "🌿", mins: 9, pids: [], date: "2026-05-15",
    city: "Santo Domingo",
    tag: { en: "Adaptógenos", es: "Adaptógenos" },
    title: {
      es: "Ashwagandha para Estrés y Rendimiento en RD: Guía Completa",
      en: "Ashwagandha for Stress and Performance in DR: Full Guide",
    },
    desc: {
      es: "Ashwagandha KSM-66 vs Sensoril: cuál elegir para reducir cortisol, mejorar sueño y subir testosterona. Dosis basadas en estudios y precios DOP.",
      en: "Ashwagandha KSM-66 vs Sensoril: which to choose to lower cortisol, improve sleep and raise testosterone. Study-based doses and DOP prices.",
    },
    headings: [
      { es: "Qué es la ashwagandha y cómo actúa", en: "What ashwagandha is and how it works" },
      { es: "KSM-66 vs Sensoril: comparativa científica", en: "KSM-66 vs Sensoril: science comparison" },
      { es: "Dosis: 300-600mg al día", en: "Dose: 300-600mg per day" },
      { es: "Quién NO debería tomarla", en: "Who should NOT take it" },
    ],
    faqs: [
      { q: { es: "¿Cuánto tarda en hacer efecto?", en: "How long to feel effects?" },
        a: { es: "Reducción de estrés en 2-4 semanas. Beneficios hormonales completos a los 60-90 días.", en: "Stress reduction in 2-4 weeks. Full hormonal benefits at 60-90 days." } },
      { q: { es: "¿Se puede tomar todos los días?", en: "Take it daily?" },
        a: { es: "Sí, los estudios usaron dosis diarias hasta 8 semanas sin efectos adversos. Descansa 1 semana cada 2 meses.", en: "Yes — studies used daily doses up to 8 weeks with no adverse effects. Rest 1 week every 2 months." } },
    ],
  },
];